import logger from '../../Utils/logger';
import React, { useEffect, useMemo, useState } from "react";
import styles from "./Style.module.css";
import { useNavigate, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  formatDate,
  getCurrentDateInSingapore,
  calculateEndTime,
  formatToSeconds,
  buildPaxesParam,
  formatToSlotLabel,
  useSticky,
} from "../../Utils/Helper";
import { rescheduleBooking } from "../../APICalling/APICalling";
import { useCheckBookingSlots, useCheckSlotAvailable, useGetOutletType } from "../../APICalling/useApiQueries";
import { computeMemberFlags } from "../../Utils/memberPricing";
import { getBookingErrorMessage } from "../../Utils/Hooks/useCreateBookingRequest";
import { TimeSlotsPicker } from "./TimeSlotsPicker";
import { Footer } from "../../Components/Cart/Footer";
import { ReschedulingExtendedPopup } from "../../Components/Popup/ReschedulingExtendedPopup/ReschedulingExtendedPopup";
import { ChangeOutletPopup } from "../../Components/Popup/ChangeOutletPopup/ChangeOutletPopup";
import { Header } from "../../Components/Header/Header";
import { transformCustomerData } from "../../Utils/transformCustomerData";
import { BookingDatesPicker } from "./BookingDatesPicker";
import { ChevronDown, Store } from "lucide-react";
import { useAppointment } from "../../Context/Appointment/AppointmentContext";
import { useLocation as useLocationContext } from "../../Context/Location/LocationContext";
import { useBookingSession } from "../../Context/BookingSession/BookingSessionContext";
import { useGuests } from "../../Context/Guests/GuestsContext";
import { Snakbar } from "../../Components/Snakbar/Snakbar";
import { useCustomer } from "../../Context/Customer/CustomerContext";
import { STORAGE_KEYS } from "../../Utils/SessionStorage";
import { useWebSettings } from "../../Context/WebSettings/WebSettingsContext";
import { resolveBookingDateTime } from "../../Utils/resolveBookingDateTime";

const parseServiceAtDateTime = (serviceAtStr) => {
  if (!serviceAtStr) return { date: null, time: null };
  const [date, timePart, period] = serviceAtStr.split(" ");
  if (!date || !timePart || !period) return { date: null, time: null };

  let [hours, minutes, seconds = "00"] = timePart.split(":");
  hours = parseInt(hours, 10);
  if (period === "PM" && hours !== 12) hours += 12;
  if (period === "AM" && hours === 12) hours = 0;

  const formattedTime = formatToSeconds(
    `${String(hours).padStart(2, "0")}:${minutes}:${seconds}`
  );
  return { date, time: formattedTime };
};

export const SelectTime = () => {
  const { setAppointmentTypeID, setActualMembership } = useAppointment();
  const { bookingID, setBookingID, uuid,setUuid,setBookingUpdate, setRescheduleCount, setRescheduleMaxLimit, setReschedulingTimeLimit } = useBookingSession();
  const { 
    setOutletTypeID, 
    outletTypeID,
    setSelectedOutletID, 
    selectedOutletContact, 
    setSelectedOutletContact, 
    selectedOutletWhatsapp,
    setSelectedOutletWhatsapp, 
    selectedOutletID 
  } = useLocationContext();
  const { setPrimaryCustomer } = useGuests();
  const { setUserID } = useCustomer();
  const { rescheduleMaxLimit: webRescheduleMaxLimit, rescheduleTimeLimit: webRescheduleTimeLimit } = useWebSettings();

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const checkSlotsMutation = useCheckBookingSlots();
  const checkSlotAvailableMutation = useCheckSlotAvailable();
  const [isUpdating, setIsUpdating] = useState(false);
  const [selectedOutlet, setSelectedOutlet] = useState(null);
  const [items, setItems] = useState([]);
  const [selectedDate, setSelectedDate] = useState(getCurrentDateInSingapore());
  const [selectedTime, setSelectedTime] = useState(null);

  const [isRescheduledExtended, setIsRescheduledExtended] = useState(false);
  const [errors, setErrors] = useState({ error: "" });

  const [isChangeOutletPopupActive, setIsChangeOutletPopupActive] = useState(false);
  const { isSticky, sentinelRef } = useSticky();
  
  
  const location = useLocation();
  const bookingData = location.state?.bookingData;
  const customerData = location.state?.customerData;
  const bookingDate = location.state?.serviceAt;
  const originalBooking = bookingData?.bookingData?.[0];
  const bookingMembership = originalBooking?.membership ?? 0;
  const bookingOutletTypeID = originalBooking?.outlet_type_id
    ?? originalBooking?.outlet_type?.[0]?.id
    ?? outletTypeID;
  const { data: outletTypeData } = useGetOutletType({ pagination: 0, status: 1 });
  const outletTypes = outletTypeData?.data?.data?.list?.outlet_type;
  const bookingOutletTypeName = (Array.isArray(outletTypes)
    ? outletTypes.find((type) => String(type.id) === String(bookingOutletTypeID))?.name
    : undefined) ?? originalBooking?.outlet_type?.[0]?.name;


  const transformedData = useMemo(
    () => transformCustomerData(customerData),
    [customerData]
  );

  const customerName = transformedData[0]?.name;

  useEffect(() => {
    if (customerName) {
      setPrimaryCustomer(customerName);
    }
  }, [customerName, setPrimaryCustomer]);

  // Track if BookingDatesPicker (day-slot-availability API) is still loading
  const [isLoadingBookingDates, setIsLoadingBookingDates] = useState(true);
  const [dateAvailability, setDateAvailability] = useState(null);
  const isDateAvailabilityKnown = dateAvailability?.date === selectedDate ||
    Object.prototype.hasOwnProperty.call(dateAvailability?.byDate || {}, selectedDate);
  const isDateAvailable = (dateAvailability?.date === selectedDate
    ? dateAvailability.available : dateAvailability?.byDate?.[selectedDate]) === true;

  useEffect(() => {
    const serviceAtValue =
      bookingDate || bookingData?.bookingData?.[0]?.service_at;
    const { date } = parseServiceAtDateTime(serviceAtValue);
    if (date) setSelectedDate(date);
  }, [bookingDate, bookingData]);

  // useEffect(() => {
  //   if (!errors.error) return;
  //   const timer = setTimeout(() => {
  //     setErrors((prev) => ({ ...prev, error: "" }));
  //   }, 4000);
  //   return () => clearTimeout(timer);
  // }, [errors.error]);


  useEffect(() => {
    if (bookingData?.bookingData?.length > 0) {
      const firstBooking = bookingData.bookingData[0];
      logger.log("firstBooking",firstBooking);
      setBookingID(firstBooking?.id);
      if (firstBooking?.uuid) setUuid(firstBooking.uuid);
      setUserID(firstBooking?.user_id);
      setAppointmentTypeID(firstBooking?.booking_type_id || 1);
      // Preserve the booking API's membership when continuing with another outlet.
      setActualMembership(firstBooking.membership ?? 0);
      setSelectedOutletID(firstBooking.outlet_id);
      setSelectedOutletContact(firstBooking.outlet_number);
      setSelectedOutletWhatsapp(firstBooking.whatsapp_number);
      // setOutletTypeID(firstBooking.outlet_type_id);
      setSelectedOutlet(firstBooking?.outlet);
      setRescheduleCount(firstBooking?.reschedule || 0);
      setRescheduleMaxLimit(
        firstBooking?.reschedule_max_limit != null
          ? Number(firstBooking.reschedule_max_limit)
          : webRescheduleMaxLimit
      );
      setReschedulingTimeLimit(
        firstBooking?.reschedule_time_limit != null
          ? Number(firstBooking.reschedule_time_limit)
          : webRescheduleTimeLimit
      );
    }
  }, [bookingData, customerData,setBookingID,setUuid,setAppointmentTypeID,setActualMembership,setSelectedOutletContact,setSelectedOutletWhatsapp,setSelectedOutlet,setRescheduleMaxLimit,setReschedulingTimeLimit,webRescheduleMaxLimit,webRescheduleTimeLimit]);



  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedTime(null);
    setItems([]);
    sessionStorage.removeItem(STORAGE_KEYS.TIME);
    setErrors((prev) => ({ ...prev, error: "" }));
  };


  const handleTimeSelect = async(time) => {
    if (!isDateAvailable || isLoadingBookingDates || isUpdating) return;
      if (!time) return;
      setSelectedTime(time);
      setErrors((prev) => ({ ...prev, error: "" }));
  
      const updatedArray = [];
      Object.keys(customerData || {}).forEach((guestKey) => {
        const services = customerData[guestKey] || [];
        let guestCursor = resolveBookingDateTime(selectedDate, time).time;
        services.forEach((item) => {
          const duration = Number(JSON.parse(item.additional || "{}").duration || 0);
          const start_time = guestCursor;
          const end_time   = calculateEndTime(guestCursor, duration);

          updatedArray.push({
            id: item.id,
            duration,
            start_time,
            end_time,
          });
          guestCursor = end_time;
        });
      });

      setItems(updatedArray);
  
    };


  // rescheduleBooking
  const hasSelectedSlot =
    Boolean(selectedTime) && Array.isArray(items) && items.length > 0;

  const refreshUnavailableSlot = async (response) => {
    setErrors((prev) => ({ ...prev, error: getBookingErrorMessage(response) }));
    setSelectedTime(null);
    setItems([]);
    sessionStorage.removeItem(STORAGE_KEYS.TIME);
    // Refresh dates first so the slot picker uses the latest day availability.
    await queryClient.resetQueries({ queryKey: ['bookingDates'] });
    await queryClient.resetQueries({ queryKey: ['bookingSlots'] });
  };

  const updateBooking = async () => {
    if (isUpdating || checkSlotsMutation.isPending) return;
    if (!selectedDate) {
      setErrors((prev) => ({ ...prev, error: "Please select the date" }));
      return;
    }
    if (!hasSelectedSlot || !isDateAvailable || isLoadingBookingDates) {
      setErrors((prev) => ({ ...prev, error: "Please select the time" }));
      return;
    }

    setIsUpdating(true);
    setErrors((prev) => ({ ...prev, error: "" }));
    try {
      const slotPayload = {
        panel: "customer",
        outlet: selectedOutletID,
        start_date: selectedDate,
        end_date: selectedDate,
        slot: formatToSlotLabel(selectedTime),
        paxes: buildPaxesParam(transformedData),
        uuid: uuid,
      };
      const checkResponse = await checkSlotsMutation.mutateAsync(slotPayload);
      if (checkResponse?.slot_not_available || checkResponse?.data?.available === false) {
        await refreshUnavailableSlot(checkResponse);
        return;
      }
      if (checkResponse?.success === false || checkResponse?.data?.success === false) {
        setErrors((prev) => ({ ...prev, error: getBookingErrorMessage(checkResponse) }));
        return;
      }

      const slotResponse = await checkSlotAvailableMutation.mutateAsync(slotPayload);
      if (slotResponse?.slot_not_available || slotResponse?.data?.available === false) {
        await refreshUnavailableSlot(slotResponse);
        return;
      }
      if (slotResponse?.success === false) {
        setErrors((prev) => ({ ...prev, error: getBookingErrorMessage(slotResponse) }));
        return;
      }

      const memberFlags = computeMemberFlags(
        bookingMembership, bookingOutletTypeID, selectedDate, selectedTime, bookingOutletTypeName,
        slotResponse?.data?.is_public_holiday ?? false
      );
      const appointment = resolveBookingDateTime(selectedDate, selectedTime);
      const itemData = {
        id: bookingID,
        items: JSON.stringify(items.map((item) => ({ ...item, ...memberFlags }))),
        service_at: `${selectedDate} ${appointment.time}`,
        panel: 'customer',
      };
      const response = await rescheduleBooking(itemData);
      if (response?.data?.success) {
        navigate(`/appointment/${uuid}`);
        return;
      }
      logger.error("Booking updated failed:", response);
    } catch (error) {
      logger.error("Error updating booking:", error);
      const errorData = error?.response?.data || error;
      if (errorData?.slot_not_available || errorData?.data?.available === false) {
        await refreshUnavailableSlot(errorData);
      } else {
        setErrors((prev) => ({ ...prev, error: getBookingErrorMessage(errorData) }));
      }
    } finally {
      setIsUpdating(false);
    }
  };


  useEffect(() => {
      const body = document.body;
      body.classList.remove(
        "overlay",
        "half",
        "overlay-index",
        "overflow-hidden"
      );
      if (isRescheduledExtended || isChangeOutletPopupActive) {
        body.classList.add("overlay", "overflow-hidden", "overlay-index");
      }
    }, [isRescheduledExtended, isChangeOutletPopupActive]);


    const backToBooking = () => {
      navigate(`/appointment/${uuid}`);
    };

    const closeChangeOutletPopup = () => {
      setIsChangeOutletPopupActive(false);
    };

    const handleChangeOutlet = () => {
      setIsChangeOutletPopupActive(false);
      setBookingUpdate(true);
      setSelectedOutletID(null);
      navigate("/outlets");
    };
  
  // REDIRECTION TO HOME WHEN VALUE IS NOT AVAILABLE
  useEffect(() => {
    if(
      bookingData == null ||
      customerData == null
    ){navigate(`/appointment/${uuid}`);}
  }, [bookingData,customerData,uuid,navigate])  


  return (
    <>
    <div className="sticky-sentinel" ref={sentinelRef} aria-hidden="true" />
    <div className={`sticky-header ${isSticky ? "is-sticky" : ""} pb-0`}>
      <Header 
        title="Reschedule Booking"
        noCross={true}
      />

      {/* Date Tabs */}
      <div className={`${styles.dateTabsContainer}`}>
        <div className={`${styles.selectedDate} container-md space-x space-y`}>
        {selectedOutlet && (
            <button
              type="button"
              className={`${styles.selectedDate} ${styles.dropdown} btn`}
              onClick={() => setIsChangeOutletPopupActive(true)}
              aria-label="Change outlet"
            >
              <Store color="var(--PrimaryColor800)" />
              {selectedOutlet?.name}
              <ChevronDown color="var(--PrimaryColor900)" />
            </button>
        )}
          {formatDate(selectedDate).monthName} {formatDate(selectedDate).yearNum}
        </div>
        <BookingDatesPicker
          className={styles.dateTabs}
          selectedOutletID={selectedOutletID}
          members={transformedData}
          selectedDate={selectedDate}
          onDateSelect={handleDateSelect}
          formatDate={formatDate}
          onLoadingChange={setIsLoadingBookingDates}
          onAvailabilityChange={setDateAvailability}
          rescheduling={true}
          uuid={uuid}
        />
      </div>
    </div>
    
    <TimeSlotsPicker
      selectedOutletID={selectedOutletID}
      members={transformedData}
      selectedDate={selectedDate}
      selectedTime={selectedTime}
      phoneNum={selectedOutletContact}
      whatsappNum={selectedOutletWhatsapp}
      onTimeSelect={handleTimeSelect}
      isWaitingForDates={isLoadingBookingDates || !isDateAvailabilityKnown}
      isDateAvailable={isDateAvailable}
      datesError={dateAvailability?.errorMessage || ""}
      onRetryDates={() => queryClient.refetchQueries({ queryKey: ['bookingDates'] })}
      rescheduling={true}
      uuid={uuid}
      isSlotChecking={isUpdating || checkSlotsMutation.isPending}
    />




      <Snakbar
        text={errors.error}
        active={errors.error}
        varient="danger"
        type="error"
        hasButton={false}
      />


      <Footer 
      btnLabel={isUpdating ? "Processing..." : "Update Appointment"} 
      btnClick={updateBooking} 
      isFooterActive={true}
      disabled={isUpdating || !hasSelectedSlot || !isDateAvailable || isLoadingBookingDates}
      noTerms={true}
      />
      {isRescheduledExtended && (
        <ReschedulingExtendedPopup
          active={isRescheduledExtended}
          handleClick={backToBooking}
          reschedulingMaxLimit={bookingData?.bookingData?.[0]?.reschedule_max_limit}
        />
      )}
      {isChangeOutletPopupActive && (
        <ChangeOutletPopup
          active={isChangeOutletPopupActive}
          onClose={closeChangeOutletPopup}
          handleChangeOutlet={handleChangeOutlet}
        />
      )}
    </>
  );
};
